import React from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';

export const NUCLEAI_ORIGINS_ROUTE_PATH = '/nucleai-origins';

export type OriginsTabValue =
  | 'models'
  | 'model-training-sessions'
  | 'datasets'
  | 'experimental-reports'
  | 'vector-repositories';

export interface OriginsSearchState {
  tab?: OriginsTabValue;
  modelId?: string | null;
  trainingSessionId?: string | null;
  datasetId?: string | null;
  datasetSnapshotTimeUtc?: string | null;
  searchTerm?: string | null;
  annotationAssignmentId?: number | null;
}

type OriginsSearchPatch = Partial<{
  tab: OriginsTabValue | null;
  modelId: string | null;
  trainingSessionId: string | null;
  datasetId: string | null;
  datasetSnapshotTimeUtc: string | null;
  searchTerm: string | null;
  annotationAssignmentId: number | string | null;
}>;

interface PreviousLocation {
  pathname: string;
  search: string;
}

export interface NucleaiOriginsRouteState {
  previousLocation?: PreviousLocation;
  originsContext?: {
    labId?: string | null;
  };
}

const parseAnnotationAssignmentId = (value: string | null) => {
  if (!value) {
    return null;
  }

  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const readOriginsSearchState = (search: string): OriginsSearchState => {
  const searchParams = new URLSearchParams(search);
  const requestedTab = searchParams.get('tab');

  return {
    tab: requestedTab as OriginsTabValue | undefined,
    modelId: searchParams.get('modelId'),
    trainingSessionId: searchParams.get('trainingSessionId'),
    datasetId: searchParams.get('datasetId'),
    datasetSnapshotTimeUtc: searchParams.get('datasetSnapshotTimeUtc'),
    searchTerm: searchParams.get('searchTerm'),
    annotationAssignmentId: parseAnnotationAssignmentId(searchParams.get('annotationAssignmentId')),
  };
};

export const buildOriginsSearch = (search: string, patch: OriginsSearchPatch) => {
  const searchParams = new URLSearchParams(search);

  Object.entries(patch).forEach(([key, value]) => {
    if (value == null || value === '') {
      searchParams.delete(key);
      return;
    }

    searchParams.set(key, String(value));
  });

  const serialized = searchParams.toString();

  return serialized ? `?${serialized}` : '';
};

export const useOriginsSearchState = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchState = React.useMemo(() => readOriginsSearchState(location.search), [location.search]);

  const locationRef = React.useRef(location);
  locationRef.current = location;

  const updateSearchState = React.useCallback(
    (patch: OriginsSearchPatch) => {
      const currentLocation = locationRef.current;
      const nextSearch = buildOriginsSearch(currentLocation.search, patch);

      if (nextSearch === (currentLocation.search || '')) {
        return;
      }

      navigate(
        {
          pathname: currentLocation.pathname,
          search: nextSearch,
        },
        {
          replace: true,
          state: currentLocation.state,
        }
      );
    },
    [navigate]
  );

  return {
    location,
    searchState,
    updateSearchState,
  };
};

export const buildNucleaiOriginsSearch = (labId?: string | null) => {
  const searchParams = new URLSearchParams();

  if (labId) {
    searchParams.set('labId', labId);
  }

  const serialized = searchParams.toString();
  return serialized ? `?${serialized}` : '';
};

export const buildNucleaiOriginsRouteState = ({
  location,
  labId,
}: {
  location: Location;
  labId?: string | null;
}): NucleaiOriginsRouteState => ({
  previousLocation: {
    pathname: location.pathname,
    search: location.search,
  },
  originsContext: {
    labId,
  },
});

export const getNucleaiOriginsCloseLocation = ({
  state,
  labId,
}: {
  state?: NucleaiOriginsRouteState | null;
  labId?: string | null;
}): PreviousLocation => {
  if (state?.previousLocation) {
    return state.previousLocation;
  }

  return {
    pathname: '/',
    search: buildNucleaiOriginsSearch(state?.originsContext?.labId || labId),
  };
};
